import React, { useState, useEffect } from 'react';
import { useAuth } from '../../common/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Bell, Clock, Phone, Save, AlertCircle, CheckCircle, Trash2, Calendar, Timer } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { API_URL } from '../../api/config';

const NotificationSettings = () => {
  const { userToken: token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [newNotification, setNewNotification] = useState({
    phoneNumber: '',
    time: '09:00',
    daysBack: 1,
    reportStartTime: '00:00',
    reportEndTime: '23:59',
    includeSalesReport: true,
    includeExpenseReport: false
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchNotificationSettings();
  }, []);

  const fetchNotificationSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/notifications/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      toast.error('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  const handleAddNotification = async () => {
    const newErrors = {};

    if (!newNotification.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!validatePhoneNumber(newNotification.phoneNumber.trim())) {
      newErrors.phoneNumber = 'Please enter a valid 10-digit Indian mobile number';
    }

    if (!newNotification.time) {
      newErrors.time = 'Notification time is required';
    }

    if (!newNotification.includeSalesReport && !newNotification.includeExpenseReport) {
      newErrors.reports = 'Select at least one report type';
    }

    // Validate time range
    if (newNotification.reportStartTime >= newNotification.reportEndTime) {
      newErrors.timeRange = 'Start time must be before end time';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsSaving(true);
      const response = await fetch(`${API_URL}/notifications/settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phoneNumber: `+91${newNotification.phoneNumber.trim()}`,
          time: newNotification.time,
          daysBack: newNotification.daysBack,
          reportStartTime: newNotification.reportStartTime,
          reportEndTime: newNotification.reportEndTime,
          includeSalesReport: newNotification.includeSalesReport,
          includeExpenseReport: newNotification.includeExpenseReport
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Notification setting added successfully!');
        setNotifications(data.notifications || []);
        setNewNotification({
          phoneNumber: '',
          time: '09:00',
          daysBack: 1,
          reportStartTime: '00:00',
          reportEndTime: '23:59',
          includeSalesReport: true,
          includeExpenseReport: false
        });
        setErrors({});
      } else {
        toast.error(data.message || 'Failed to add notification setting');
      }
    } catch (error) {
      console.error('Error adding notification:', error);
      toast.error('Failed to add notification setting');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    if (!window.confirm('Are you sure you want to delete this notification setting?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/notifications/settings/${notificationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Notification setting deleted successfully!');
        setNotifications(notifications.filter(n => n._id !== notificationId));
      } else {
        const data = await response.json();
        toast.error(data.message || 'Failed to delete notification setting');
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to delete notification setting');
    }
  };

  const handleToggleActive = async (notificationId, currentStatus) => {
    try {
      const response = await fetch(`${API_URL}/notifications/settings/${notificationId}/toggle`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isActive: !currentStatus })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Notification ${!currentStatus ? 'enabled' : 'disabled'} successfully!`);
        setNotifications(data.notifications || []);
      } else {
        const data = await response.json();
        toast.error(data.message || 'Failed to update notification setting');
      }
    } catch (error) {
      console.error('Error toggling notification:', error);
      toast.error('Failed to update notification setting');
    }
  };

  const formatTime = (time24) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getDayLabel = (daysBack) => {
    if (daysBack === 0) return 'Today';
    if (daysBack === 1) return 'Yesterday';
    if (daysBack === 2) return 'Day Before Yesterday';
    return `${daysBack} days ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading notification settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <ToastContainer position="top-right" autoClose={5000} />
      
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Bell className="text-indigo-600" size={28} />
            <h1 className="text-2xl font-bold text-gray-800">WhatsApp Notifications</h1>
          </div>
          <p className="text-gray-600">Get customized sales and expense reports on WhatsApp</p>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg">
          <div className="flex items-start">
            <AlertCircle className="text-blue-500 mt-0.5 mr-3 flex-shrink-0" size={20} />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">How it works:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Choose which day's report to receive (today, yesterday, or up to 7 days back)</li>
                <li>Set custom time range for the report (e.g., 12:00 AM - 11:59 PM)</li>
                <li>Notifications are sent daily at your specified time</li>
                <li>You can configure multiple notifications for different time periods</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Add New Notification */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Add New Notification</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 text-gray-500 bg-gray-50 border border-r-0 border-gray-300 rounded-l-md">
                  <Phone size={16} className="mr-1" />
                  +91
                </span>
                <input
                  type="tel"
                  maxLength={10}
                  placeholder="10-digit mobile number"
                  className={`flex-1 px-3 py-2 border rounded-r-md ${
                    errors.phoneNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                  value={newNotification.phoneNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setNewNotification({ ...newNotification, phoneNumber: value });
                    setErrors({ ...errors, phoneNumber: '' });
                  }}
                />
              </div>
              {errors.phoneNumber && (
                <p className="text-sm text-red-500 mt-1">{errors.phoneNumber}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notification Time <span className="text-red-500">*</span>
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 text-gray-500 bg-gray-50 border border-r-0 border-gray-300 rounded-l-md">
                  <Clock size={16} />
                </span>
                <input
                  type="time"
                  className={`flex-1 px-3 py-2 border rounded-r-md ${
                    errors.time ? 'border-red-500' : 'border-gray-300'
                  }`}
                  value={newNotification.time}
                  onChange={(e) => {
                    setNewNotification({ ...newNotification, time: e.target.value });
                    setErrors({ ...errors, time: '' });
                  }}
                />
              </div>
              {errors.time && (
                <p className="text-sm text-red-500 mt-1">{errors.time}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Notification will be sent at {formatTime(newNotification.time)}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Report For <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-gray-500" />
                <select
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                  value={newNotification.daysBack}
                  onChange={(e) => {
                    setNewNotification({ ...newNotification, daysBack: parseInt(e.target.value) });
                  }}
                >
                  <option value={0}>Today</option>
                  <option value={1}>Yesterday</option>
                  <option value={2}>Day Before Yesterday</option>
                  <option value={3}>3 Days Ago</option>
                  <option value={4}>4 Days Ago</option>
                  <option value={5}>5 Days Ago</option>
                  <option value={6}>6 Days Ago</option>
                  <option value={7}>7 Days Ago</option>
                </select>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Report will include data for {getDayLabel(newNotification.daysBack)}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Report Time Range <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">From</label>
                  <div className="flex">
                    <span className="inline-flex items-center px-2 text-gray-500 bg-gray-50 border border-r-0 border-gray-300 rounded-l-md">
                      <Timer size={14} />
                    </span>
                    <input
                      type="time"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md text-sm"
                      value={newNotification.reportStartTime}
                      onChange={(e) => {
                        setNewNotification({ ...newNotification, reportStartTime: e.target.value });
                        setErrors({ ...errors, timeRange: '' });
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{formatTime(newNotification.reportStartTime)}</p>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">To</label>
                  <div className="flex">
                    <span className="inline-flex items-center px-2 text-gray-500 bg-gray-50 border border-r-0 border-gray-300 rounded-l-md">
                      <Timer size={14} />
                    </span>
                    <input
                      type="time"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md text-sm"
                      value={newNotification.reportEndTime}
                      onChange={(e) => {
                        setNewNotification({ ...newNotification, reportEndTime: e.target.value });
                        setErrors({ ...errors, timeRange: '' });
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{formatTime(newNotification.reportEndTime)}</p>
                </div>
              </div>
              {errors.timeRange && (
                <p className="text-sm text-red-500 mt-1">{errors.timeRange}</p>
              )}
              <p className="text-xs text-gray-500 mt-2">
                📊 Report will cover: {formatTime(newNotification.reportStartTime)} - {formatTime(newNotification.reportEndTime)}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Report Types <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    checked={newNotification.includeSalesReport}
                    onChange={(e) => {
                      setNewNotification({ ...newNotification, includeSalesReport: e.target.checked });
                      setErrors({ ...errors, reports: '' });
                    }}
                  />
                  <span className="ml-2 text-sm text-gray-700">Daily Sales Report</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    checked={newNotification.includeExpenseReport}
                    onChange={(e) => {
                      setNewNotification({ ...newNotification, includeExpenseReport: e.target.checked });
                      setErrors({ ...errors, reports: '' });
                    }}
                  />
                  <span className="ml-2 text-sm text-gray-700">Daily Expense Report</span>
                </label>
              </div>
              {errors.reports && (
                <p className="text-sm text-red-500 mt-1">{errors.reports}</p>
              )}
            </div>

            <button
              onClick={handleAddNotification}
              disabled={isSaving}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md text-white ${
                isSaving ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'
              }`}
            >
              <Save size={18} />
              {isSaving ? 'Adding...' : 'Add Notification'}
            </button>
          </div>
        </div>

        {/* Existing Notifications */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Your Notifications</h2>
          
          {notifications.length === 0 ? (
            <div className="text-center py-8">
              <Bell className="mx-auto text-gray-300 mb-3" size={48} />
              <p className="text-gray-500">No notifications configured yet</p>
              <p className="text-sm text-gray-400 mt-1">Add your first notification above to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {notifications.map((notification) => (
                <div
                  key={notification._id}
                  className={`border rounded-lg p-4 ${
                    notification.isActive ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Phone size={16} className="text-gray-500" />
                        <span className="font-medium text-gray-800">{notification.phoneNumber}</span>
                        {notification.isActive ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded-full">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="space-y-1 text-sm text-gray-600 mb-2">
                        <div className="flex items-center gap-2">
                          <Clock size={14} />
                          <span>Sends at {formatTime(notification.time)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar size={14} />
                          <span>Report for: {getDayLabel(notification.daysBack || 1)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Timer size={14} />
                          <span>
                            Time range: {formatTime(notification.reportStartTime || '00:00')} - {formatTime(notification.reportEndTime || '23:59')}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {notification.includeSalesReport && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                            Sales Report
                          </span>
                        )}
                        {notification.includeExpenseReport && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
                            Expense Report
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleToggleActive(notification._id, notification.isActive)}
                        className={`p-2 rounded-md ${
                          notification.isActive
                            ? 'text-gray-600 hover:bg-gray-200'
                            : 'text-green-600 hover:bg-green-100'
                        }`}
                        title={notification.isActive ? 'Disable' : 'Enable'}
                      >
                        <CheckCircle size={20} />
                      </button>
                      <button
                        onClick={() => handleDeleteNotification(notification._id)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-md"
                        title="Delete"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
